import React, { useEffect, useMemo, useState, useRef } from "react";
import { addDaysToDate, DEFAULT_DUE_DAYS, useAppState } from "../../hooks/useAppState";
import { useAuth } from "../../hooks/useAuth";
import { subscribeQuotes } from "../../services/quotesService";

const DRAFT_KEY = "forfy_invoice_draft";

const INVOICE_TYPES = [
  { id: "standard", label: "Standard" },
  { id: "acconto", label: "Acconto" },
  { id: "saldo", label: "Saldo" },
  { id: "parziale", label: "Parziale" },
];

const ACCONTO_MIN_PCT = 10;
const ACCONTO_MAX_PCT = 90;

function normalizeName(s) {
  return (s || "").trim().replace(/\s+/g, " ").toLowerCase();
}

function parseNum(value) {
  if (typeof value === "number") return value;
  const n = Number(String(value ?? "").replace(",", "."));
  return Number.isFinite(n) ? n : NaN;
}

function formatCurrency(value) {
  const n = parseNum(value);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}
const DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24h per ripristino
const DRAFT_CLEANUP_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7gg per cleanup

function todayISO() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function getDraft() {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function saveDraft(data) {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify({ ...data, savedAt: Date.now() }));
  } catch { /* ignore quota errors */ }
}

function clearDraft() {
  localStorage.removeItem(DRAFT_KEY);
}

export default function InvoiceModal({
  open,
  mode, // "create" | "edit" | "duplicate"
  initialValues,
  nextSeq,
  year,
  clientCatalog = [],
  onClose,
  onSubmit,
  darkMode = false,
}) {
  const [issueDate, setIssueDate] = useState(todayISO());
  const [dueDate, setDueDate] = useState(addDaysToDate(todayISO(), DEFAULT_DUE_DAYS));
  const [dueDateManuallyEdited, setDueDateManuallyEdited] = useState(false);
  const [invoiceCode, setInvoiceCode] = useState("");
  const [clientName, setClientName] = useState("");
  const [grossAmount, setGrossAmount] = useState("");
  const [paid, setPaid] = useState(true);
  const [paymentDate, setPaymentDate] = useState("");
  const [notes, setNotes] = useState("");
  const [showClientSuggestions, setShowClientSuggestions] = useState(false);
  const [dueDateError, setDueDateError] = useState("");

  // Tipo fattura: standard | acconto | saldo | parziale
  const [invoiceType, setInvoiceType] = useState("standard");
  const [accontoPercentage, setAccontoPercentage] = useState("");
  const [accontoTotalePrevisto, setAccontoTotalePrevisto] = useState("");
  const [saldoParentInvoiceId, setSaldoParentInvoiceId] = useState("");
  const [saldoTotalePrevisto, setSaldoTotalePrevisto] = useState("");
  const [parzialeQuoteId, setParzialeQuoteId] = useState("");
  const [amountManuallyEdited, setAmountManuallyEdited] = useState(false);
  const [typeError, setTypeError] = useState("");

  // Fatture esistenti (per trovare gli acconti dello stesso cliente) e preventivi accettati
  const { invoices: allInvoices } = useAppState();
  const { user: authUser } = useAuth();
  const [quotes, setQuotes] = useState([]);

  useEffect(() => {
    if (!open || !authUser?.uid) return;
    const unsub = subscribeQuotes(authUser.uid, (data) => setQuotes(data || []));
    return () => unsub();
  }, [open, authUser?.uid]);

  const currentId = mode === "edit" ? initialValues?.id : null;

  // Fatture di tipo acconto dello stesso cliente (per collegare un Saldo)
  const clientAccontoInvoices = useMemo(() => {
    const name = normalizeName(clientName);
    if (!name) return [];
    return (allInvoices || []).filter(
      (inv) =>
        inv.invoiceType === "acconto" &&
        normalizeName(inv.clientName) === name &&
        inv.id !== currentId
    );
  }, [allInvoices, clientName, currentId]);

  // Preventivi accettati dello stesso cliente (per collegare una fattura Parziale)
  const clientAcceptedQuotes = useMemo(() => {
    const name = normalizeName(clientName);
    if (!name) return [];
    return (quotes || []).filter(
      (q) => q.status === "accepted" && normalizeName(q.clientName) === name
    );
  }, [quotes, clientName]);

  // Somma degli acconti già incassati collegati allo stesso cliente (per calcolo Saldo)
  const paidAccontiSum = useMemo(() => {
    return clientAccontoInvoices
      .filter((inv) => inv.paid)
      .reduce((sum, inv) => sum + (parseNum(inv.grossAmount) || 0), 0);
  }, [clientAccontoInvoices]);

  const selectedSaldoParent = useMemo(
    () => clientAccontoInvoices.find((inv) => inv.id === saldoParentInvoiceId) || null,
    [clientAccontoInvoices, saldoParentInvoiceId]
  );

  const selectedParzialeQuote = useMemo(
    () => clientAcceptedQuotes.find((q) => q.id === parzialeQuoteId) || null,
    [clientAcceptedQuotes, parzialeQuoteId]
  );

  // Selezionando una fattura di acconto collegata, prova a derivare il "totale previsto"
  // dal suo importo e dalla percentuale salvata (se disponibile).
  useEffect(() => {
    if (invoiceType !== "saldo" || !selectedSaldoParent) return;
    const pct = parseNum(selectedSaldoParent.percentageOfTotal);
    const parentAmount = parseNum(selectedSaldoParent.grossAmount);
    if (pct > 0 && Number.isFinite(parentAmount)) {
      const derived = parentAmount / (pct / 100);
      setSaldoTotalePrevisto(derived.toFixed(2));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSaldoParent, invoiceType]);

  // Calcolo automatico dell'importo lordo in base al tipo fattura
  useEffect(() => {
    if (amountManuallyEdited) return;

    if (invoiceType === "acconto") {
      const totale = parseNum(accontoTotalePrevisto);
      const pct = parseNum(accontoPercentage);
      if (totale > 0 && pct > 0) {
        setGrossAmount((totale * (pct / 100)).toFixed(2));
      } else {
        setGrossAmount("");
      }
    } else if (invoiceType === "saldo") {
      const totale = parseNum(saldoTotalePrevisto);
      if (totale > 0) {
        const suggested = totale - paidAccontiSum;
        setGrossAmount(suggested > 0 ? suggested.toFixed(2) : "0");
      } else {
        setGrossAmount("");
      }
    } else if (invoiceType === "parziale") {
      if (selectedParzialeQuote) {
        setGrossAmount(String(parseNum(selectedParzialeQuote.total) || 0));
      } else {
        setGrossAmount("");
      }
    }
  }, [
    invoiceType,
    accontoTotalePrevisto,
    accontoPercentage,
    saldoTotalePrevisto,
    paidAccontiSum,
    selectedParzialeQuote,
    amountManuallyEdited,
  ]);

  // Draft state
  const [showDraftDialog, setShowDraftDialog] = useState(false);
  const [existingDraft, setExistingDraft] = useState(null);
  const skipAutoSave = useRef(false);

  // Cleanup bozze vecchie > 7 giorni al mount
  useEffect(() => {
    const draft = getDraft();
    if (draft && draft.savedAt && (Date.now() - draft.savedAt > DRAFT_CLEANUP_AGE_MS)) {
      clearDraft();
    }
  }, []);

  // Rilevamento bozza all'apertura in create mode
  useEffect(() => {
    if (!open) return;

    if (mode === "create") {
      const draft = getDraft();
      if (draft && draft.savedAt && (Date.now() - draft.savedAt < DRAFT_MAX_AGE_MS)) {
        const hasContent = draft.clientName || draft.grossAmount || draft.notes;
        if (hasContent) {
          setExistingDraft(draft);
          setShowDraftDialog(true);
          return;
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode]);

  useEffect(() => {
    if (!open) return;

    if ((mode === "edit" || mode === "duplicate" || mode === "annulla") && initialValues) {
      // duplicate/annulla = nuova fattura da template: data oggi, numero vuoto.
      const isNewCopy = mode === "duplicate" || mode === "annulla";
      const newIssueDate = isNewCopy ? todayISO() : (initialValues.issueDate || todayISO());
      setIssueDate(newIssueDate);

      if (isNewCopy) {
        setDueDate(addDaysToDate(newIssueDate, DEFAULT_DUE_DAYS));
        setDueDateManuallyEdited(false);
      } else {
        setDueDate(initialValues.dueDate || addDaysToDate(newIssueDate, DEFAULT_DUE_DAYS));
        setDueDateManuallyEdited(true);
      }

      if (isNewCopy) {
        setInvoiceCode("");
      } else {
        setInvoiceCode(initialValues.invoiceCode || "");
      }

      setClientName(initialValues.clientName || "");
      setGrossAmount(String(initialValues.grossAmount ?? ""));
      setPaid(Boolean(initialValues.paid));
      setPaymentDate(initialValues.paymentDate || "");
      setNotes(initialValues.notes || "");
      setDueDateError("");

      const type = initialValues.invoiceType || "standard";
      setInvoiceType(type);
      setTypeError("");
      if (isNewCopy) {
        // Duplica: mantiene il tipo ma non i collegamenti (da riselezionare esplicitamente)
        setSaldoParentInvoiceId("");
        setParzialeQuoteId("");
        setSaldoTotalePrevisto("");
        if (type === "acconto") {
          const pct = initialValues.percentageOfTotal;
          setAccontoPercentage(pct != null ? String(pct) : "");
          const amt = parseNum(initialValues.grossAmount);
          setAccontoTotalePrevisto(pct > 0 && Number.isFinite(amt) ? (amt / (pct / 100)).toFixed(2) : "");
        } else {
          setAccontoPercentage("");
          setAccontoTotalePrevisto("");
        }
        setAmountManuallyEdited(false);
      } else {
        // Edit: ripristina i campi collegati e blocca il ricalcolo automatico
        setSaldoParentInvoiceId(initialValues.parentInvoiceId || "");
        setParzialeQuoteId(initialValues.quoteId || "");
        const pct = initialValues.percentageOfTotal;
        const amt = parseNum(initialValues.grossAmount);
        if (type === "acconto") {
          setAccontoPercentage(pct != null ? String(pct) : "");
          setAccontoTotalePrevisto(pct > 0 && Number.isFinite(amt) ? (amt / (pct / 100)).toFixed(2) : "");
        } else {
          setAccontoPercentage("");
          setAccontoTotalePrevisto("");
        }
        setSaldoTotalePrevisto(""); // per "saldo" viene derivato dall'effect quando disponibile il parent
        setAmountManuallyEdited(true);
      }
      return;
    }

    // create - reset form (bozza gestita separatamente dal dialog)
    if (!showDraftDialog) {
      const today = todayISO();
      setIssueDate(today);
      setDueDate(addDaysToDate(today, DEFAULT_DUE_DAYS));
      setDueDateManuallyEdited(false);
      setInvoiceCode("");
      setClientName("");
      setGrossAmount("");
      setPaid(true);
      setPaymentDate("");
      setNotes("");
      setDueDateError("");
      setInvoiceType("standard");
      setAccontoPercentage("");
      setAccontoTotalePrevisto("");
      setSaldoParentInvoiceId("");
      setSaldoTotalePrevisto("");
      setParzialeQuoteId("");
      setAmountManuallyEdited(false);
      setTypeError("");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, initialValues]);

  // Cambio tipo fattura: reset dei campi specifici del tipo precedente
  const handleInvoiceTypeChange = (newType) => {
    setInvoiceType(newType);
    setTypeError("");
    setAmountManuallyEdited(false);
    if (newType !== "acconto") {
      setAccontoPercentage("");
      setAccontoTotalePrevisto("");
    }
    if (newType !== "saldo") {
      setSaldoParentInvoiceId("");
      setSaldoTotalePrevisto("");
    }
    if (newType !== "parziale") {
      setParzialeQuoteId("");
    }
    if (newType === "standard") {
      setGrossAmount("");
    }
  };

  const handleAmountChange = (value) => {
    setGrossAmount(value);
    if (invoiceType !== "standard") setAmountManuallyEdited(true);
  };

  // Auto-save bozza in mode create
  useEffect(() => {
    if (!open || mode !== "create" || skipAutoSave.current || showDraftDialog) return;
    const hasContent = clientName || grossAmount || notes;
    if (!hasContent) return;

    saveDraft({ issueDate, dueDate, invoiceCode, clientName, grossAmount, paid, paymentDate, notes });
  }, [open, mode, issueDate, dueDate, invoiceCode, clientName, grossAmount, paid, paymentDate, notes, showDraftDialog]);

  // Draft dialog handlers
  const restoreDraft = () => {
    if (existingDraft) {
      setIssueDate(existingDraft.issueDate || todayISO());
      setDueDate(existingDraft.dueDate || addDaysToDate(todayISO(), DEFAULT_DUE_DAYS));
      setInvoiceCode(existingDraft.invoiceCode || "");
      setClientName(existingDraft.clientName || "");
      setGrossAmount(existingDraft.grossAmount || "");
      setPaid(existingDraft.paid !== undefined ? existingDraft.paid : true);
      setPaymentDate(existingDraft.paymentDate || "");
      setNotes(existingDraft.notes || "");
    }
    setShowDraftDialog(false);
    setExistingDraft(null);
  };

  const discardDraft = () => {
    clearDraft();
    skipAutoSave.current = true;
    const today = todayISO();
    setIssueDate(today);
    setDueDate(addDaysToDate(today, DEFAULT_DUE_DAYS));
    setDueDateManuallyEdited(false);
    setInvoiceCode("");
    setClientName("");
    setGrossAmount("");
    setPaid(true);
    setPaymentDate("");
    setNotes("");
    setDueDateError("");
    setShowDraftDialog(false);
    setExistingDraft(null);
    // Re-enable auto-save after a tick
    setTimeout(() => { skipAutoSave.current = false; }, 100);
  };

  const deleteDraftAndClose = () => {
    clearDraft();
    setShowDraftDialog(false);
    setExistingDraft(null);
    onClose();
  };

  const handleIssueDateChange = (newDate) => {
    setIssueDate(newDate);

    if (!dueDateManuallyEdited && newDate) {
      const newDueDate = addDaysToDate(newDate, DEFAULT_DUE_DAYS);
      setDueDate(newDueDate);
      setDueDateError("");
    }
  };

  const handleDueDateChange = (newDueDate) => {
    setDueDate(newDueDate);
    setDueDateManuallyEdited(true);

    if (newDueDate && issueDate && newDueDate < issueDate) {
      setDueDateError("La scadenza non può essere prima della data di emissione");
    } else {
      setDueDateError("");
    }
  };

  const handleInvoiceCodeChange = (newCode) => {
    setInvoiceCode(newCode);
  };

  if (!open) return null;

  const submit = (e) => {
    e.preventDefault();

    const amt = Number(String(grossAmount).replace(",", "."));
    const amountInvalid = !Number.isFinite(amt) || (mode === "annulla" ? amt === 0 : amt <= 0);
    if (!issueDate || !invoiceCode.trim() || amountInvalid) {
      alert(mode === "annulla"
        ? "Inserisci data, numero fattura e importo (diverso da 0)."
        : "Inserisci data, numero fattura e importo valido (> 0).");
      return;
    }

    if (dueDate && dueDate < issueDate) {
      setDueDateError("La scadenza non può essere prima della data di emissione");
      return;
    }

    // Validazioni specifiche per tipo fattura
    setTypeError("");
    if (invoiceType === "acconto") {
      const pct = parseNum(accontoPercentage);
      if (!Number.isFinite(pct) || pct < ACCONTO_MIN_PCT || pct > ACCONTO_MAX_PCT) {
        setTypeError(`La percentuale dell'acconto deve essere tra ${ACCONTO_MIN_PCT}% e ${ACCONTO_MAX_PCT}%.`);
        return;
      }
      const totale = parseNum(accontoTotalePrevisto);
      if (!Number.isFinite(totale) || totale <= 0) {
        setTypeError("Inserisci il totale previsto per calcolare l'acconto.");
        return;
      }
    }

    if (invoiceType === "saldo") {
      if (!saldoParentInvoiceId) {
        setTypeError("Seleziona la fattura di acconto collegata al saldo.");
        return;
      }
      const totale = parseNum(saldoTotalePrevisto);
      if (Number.isFinite(totale) && totale > 0) {
        const impegnato = paidAccontiSum + amt;
        if (impegnato - totale > 0.01) {
          setTypeError("Acconto + saldo superano il totale previsto. Controlla gli importi.");
          return;
        }
      }
    }

    if (invoiceType === "parziale" && !parzialeQuoteId) {
      setTypeError("Seleziona il preventivo accettato da cui generare la fattura parziale.");
      return;
    }

    // Pulisci bozza al submit con successo
    clearDraft();

    onSubmit({
      issueDate,
      dueDate: dueDate || addDaysToDate(issueDate, DEFAULT_DUE_DAYS),
      invoiceCode: invoiceCode.trim(),
      seq: nextSeq,
      clientName: clientName.trim(),
      grossAmount: amt,
      paid,
      paymentDate: paid ? (paymentDate || issueDate) : "",
      notes: notes.trim(),
      invoiceType,
      parentInvoiceId: invoiceType === "saldo" ? saldoParentInvoiceId : null,
      quoteId: invoiceType === "parziale" ? parzialeQuoteId : null,
      percentageOfTotal: invoiceType === "acconto" ? parseNum(accontoPercentage) : null,
    });
  };

  const title =
    mode === "edit" ? "Modifica fattura"
    : mode === "duplicate" ? "Duplica fattura"
    : mode === "annulla" ? "Nota di credito"
    : "Nuova fattura";

  const primaryLabel =
    mode === "edit" ? "Salva"
    : mode === "duplicate" ? "Duplica"
    : mode === "annulla" ? "Crea nota di credito"
    : "Crea";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      <div className={`relative w-full max-w-lg rounded-2xl shadow-xl border p-5 ${
        darkMode ? 'bg-neutral-900 border-neutral-800' : 'bg-white border-neutral-200'
      }`}>
        <div className="flex items-start justify-between">
          <div>
            <h3 className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-neutral-900'}`}>{title}</h3>
            <p className={`text-xs mt-1 ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
              Il numero è una stringa: può includere prefissi/suffissi (es. CC-01-2026).
            </p>
          </div>
          <button
            className={`p-2 rounded-lg ${darkMode ? 'hover:bg-neutral-800 text-neutral-500' : 'hover:bg-neutral-100 text-neutral-500'}`}
            onClick={onClose}
            aria-label="Chiudi"
            title="Chiudi"
          >
            ✕
          </button>
        </div>

        {/* Draft restore dialog */}
        {showDraftDialog && (
          <div className={`mt-3 p-3 rounded-lg border ${
            darkMode ? 'bg-amber-900/30 border-amber-700 text-amber-300' : 'bg-amber-50 border-amber-200 text-amber-800'
          }`}>
            <p className="text-sm font-medium mb-2">Bozza trovata</p>
            <p className={`text-xs mb-3 ${darkMode ? 'text-amber-400' : 'text-amber-700'}`}>
              Hai una bozza non salvata{existingDraft?.clientName ? ` per "${existingDraft.clientName}"` : ""}. Vuoi ripristinarla?
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={restoreDraft}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                  darkMode ? 'bg-amber-700 text-white hover:bg-amber-600' : 'bg-amber-500 text-white hover:bg-amber-600'
                }`}
              >
                Ripristina
              </button>
              <button
                type="button"
                onClick={discardDraft}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${
                  darkMode ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-800' : 'border-neutral-300 text-neutral-700 hover:bg-neutral-100'
                }`}
              >
                Nuova fattura
              </button>
              <button
                type="button"
                onClick={deleteDraftAndClose}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                  darkMode ? 'text-red-400 hover:bg-red-900/30' : 'text-red-600 hover:bg-red-50'
                }`}
              >
                Elimina bozza
              </button>
            </div>
          </div>
        )}

        <form onSubmit={submit} className="mt-4 space-y-3">
          {/* Riga 1: Data emissione e Scadenza */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-600'}`}>Data emissione</label>
              <input
                type="date"
                className={`mt-1 w-full px-3 py-2 border rounded-lg text-sm ${
                  darkMode
                    ? 'border-neutral-700 bg-neutral-800 text-white'
                    : 'border-neutral-200 bg-white text-neutral-900'
                }`}
                value={issueDate}
                onChange={(e) => handleIssueDateChange(e.target.value)}
                required
              />
            </div>

            <div>
              <label className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-600'}`}>Scadenza</label>
              <input
                type="date"
                className={`mt-1 w-full px-3 py-2 border rounded-lg text-sm ${
                  dueDateError
                    ? 'border-red-500 bg-red-50'
                    : darkMode
                      ? 'border-neutral-700 bg-neutral-800 text-white'
                      : 'border-neutral-200 bg-white text-neutral-900'
                }`}
                value={dueDate}
                onChange={(e) => handleDueDateChange(e.target.value)}
                min={issueDate}
              />
              {dueDateError && (
                <p className="text-xs text-red-500 mt-1">{dueDateError}</p>
              )}
              {!dueDateError && !dueDateManuallyEdited && (
                <p className={`text-[11px] mt-1 ${darkMode ? 'text-neutral-600' : 'text-neutral-400'}`}>
                  Auto: emissione + {DEFAULT_DUE_DAYS} giorni
                </p>
              )}
            </div>
          </div>

          {/* Riga 2: Numero fattura */}
          <div>
            <label className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-600'}`}>Numero fattura</label>
            <input
              className={`mt-1 w-full px-3 py-2 border rounded-lg text-sm ${
                darkMode
                  ? 'border-neutral-700 bg-neutral-800 text-white'
                  : 'border-neutral-200 bg-white text-neutral-900'
              }`}
              value={invoiceCode}
              onChange={(e) => handleInvoiceCodeChange(e.target.value)}
              required
            />
          </div>

          <div className="relative">
            <label className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-600'}`}>Cliente</label>
            <input
              className={`mt-1 w-full px-3 py-2 border rounded-lg text-sm ${
                darkMode
                  ? 'border-neutral-700 bg-neutral-800 text-white'
                  : 'border-neutral-200 bg-white text-neutral-900'
              }`}
              value={clientName}
              onChange={(e) => {
                setClientName(e.target.value);
                setShowClientSuggestions(true);
              }}
              onFocus={() => setShowClientSuggestions(true)}
              onBlur={() => {
                setTimeout(() => setShowClientSuggestions(false), 150);
              }}
              placeholder="Es. ACME Srl"
              autoComplete="off"
            />
            {showClientSuggestions && clientCatalog.length > 0 && (
              <div className={`absolute z-50 mt-1 w-full border rounded-lg shadow-lg max-h-48 overflow-y-auto ${
                darkMode ? 'bg-neutral-800 border-neutral-700' : 'bg-white border-neutral-300'
              }`}>
                {clientCatalog
                  .filter((c) =>
                    c.toLowerCase().includes((clientName || "").toLowerCase())
                  )
                  .slice(0, 8)
                  .map((c) => (
                    <div
                      key={c}
                      className={`px-3 py-2 text-sm cursor-pointer ${
                        darkMode
                          ? 'text-white hover:bg-neutral-700'
                          : 'text-neutral-900 hover:bg-neutral-100'
                      }`}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setClientName(c);
                        setShowClientSuggestions(false);
                      }}
                    >
                      {c}
                    </div>
                  ))}
              </div>
            )}
            <p className={`text-[11px] mt-1 ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
              Autocomplete: ti aiuta a non scrivere lo stesso cliente in modo diverso.
            </p>
          </div>

          {/* Tipo fattura */}
          <div>
            <label className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-600'}`}>Tipo fattura</label>
            <select
              className={`mt-1 w-full px-3 py-2 border rounded-lg text-sm ${
                darkMode
                  ? 'border-neutral-700 bg-neutral-800 text-white'
                  : 'border-neutral-200 bg-white text-neutral-900'
              }`}
              value={invoiceType}
              onChange={(e) => handleInvoiceTypeChange(e.target.value)}
            >
              {INVOICE_TYPES.map((t) => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
          </div>

          {/* Acconto: percentuale + totale previsto */}
          {invoiceType === "acconto" && (
            <div className={`p-3 rounded-lg border space-y-3 ${
              darkMode ? 'border-neutral-700 bg-neutral-800/50' : 'border-neutral-200 bg-neutral-50'
            }`}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-600'}`}>
                    Percentuale ({ACCONTO_MIN_PCT}–{ACCONTO_MAX_PCT}%)
                  </label>
                  <input
                    type="number"
                    min={ACCONTO_MIN_PCT}
                    max={ACCONTO_MAX_PCT}
                    className={`mt-1 w-full px-3 py-2 border rounded-lg text-sm ${
                      darkMode ? 'border-neutral-700 bg-neutral-800 text-white' : 'border-neutral-200 bg-white text-neutral-900'
                    }`}
                    value={accontoPercentage}
                    onChange={(e) => { setAccontoPercentage(e.target.value); setAmountManuallyEdited(false); }}
                    placeholder="30"
                  />
                </div>
                <div>
                  <label className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-600'}`}>Totale previsto (€)</label>
                  <input
                    className={`mt-1 w-full px-3 py-2 border rounded-lg text-sm ${
                      darkMode ? 'border-neutral-700 bg-neutral-800 text-white' : 'border-neutral-200 bg-white text-neutral-900'
                    }`}
                    value={accontoTotalePrevisto}
                    onChange={(e) => { setAccontoTotalePrevisto(e.target.value); setAmountManuallyEdited(false); }}
                    placeholder="4000,00"
                  />
                </div>
              </div>
              <p className={`text-[11px] ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
                Importo calcolato automaticamente: totale previsto × percentuale.
              </p>
            </div>
          )}

          {/* Saldo: fattura di acconto collegata */}
          {invoiceType === "saldo" && (
            <div className={`p-3 rounded-lg border space-y-3 ${
              darkMode ? 'border-neutral-700 bg-neutral-800/50' : 'border-neutral-200 bg-neutral-50'
            }`}>
              <div>
                <label className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-600'}`}>Fattura di acconto collegata</label>
                <select
                  className={`mt-1 w-full px-3 py-2 border rounded-lg text-sm ${
                    darkMode ? 'border-neutral-700 bg-neutral-800 text-white' : 'border-neutral-200 bg-white text-neutral-900'
                  }`}
                  value={saldoParentInvoiceId}
                  onChange={(e) => { setSaldoParentInvoiceId(e.target.value); setAmountManuallyEdited(false); }}
                >
                  <option value="">— Seleziona —</option>
                  {clientAccontoInvoices.map((inv) => (
                    <option key={inv.id} value={inv.id}>
                      {inv.invoiceCode || "senza numero"} · {formatCurrency(inv.grossAmount)} · {inv.paid ? "incassata" : "non incassata"}
                    </option>
                  ))}
                </select>
                {clientAccontoInvoices.length === 0 && (
                  <p className="text-[11px] mt-1 text-amber-500">
                    Nessun acconto trovato per "{clientName || "questo cliente"}". Crea prima la fattura di acconto.
                  </p>
                )}
              </div>
              <div>
                <label className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-600'}`}>Totale previsto (€)</label>
                <input
                  className={`mt-1 w-full px-3 py-2 border rounded-lg text-sm ${
                    darkMode ? 'border-neutral-700 bg-neutral-800 text-white' : 'border-neutral-200 bg-white text-neutral-900'
                  }`}
                  value={saldoTotalePrevisto}
                  onChange={(e) => { setSaldoTotalePrevisto(e.target.value); setAmountManuallyEdited(false); }}
                  placeholder="4000,00"
                />
              </div>
              <p className={`text-[11px] ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
                Importo suggerito: totale previsto − acconti già incassati ({formatCurrency(paidAccontiSum)}).
              </p>
            </div>
          )}

          {/* Parziale: preventivo collegato */}
          {invoiceType === "parziale" && (
            <div className={`p-3 rounded-lg border space-y-3 ${
              darkMode ? 'border-neutral-700 bg-neutral-800/50' : 'border-neutral-200 bg-neutral-50'
            }`}>
              <div>
                <label className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-600'}`}>Da preventivo (accettato)</label>
                <select
                  className={`mt-1 w-full px-3 py-2 border rounded-lg text-sm ${
                    darkMode ? 'border-neutral-700 bg-neutral-800 text-white' : 'border-neutral-200 bg-white text-neutral-900'
                  }`}
                  value={parzialeQuoteId}
                  onChange={(e) => { setParzialeQuoteId(e.target.value); setAmountManuallyEdited(false); }}
                >
                  <option value="">— Seleziona —</option>
                  {clientAcceptedQuotes.map((q) => (
                    <option key={q.id} value={q.id}>
                      {q.number || "senza numero"} · {formatCurrency(q.total)}
                    </option>
                  ))}
                </select>
                {clientAcceptedQuotes.length === 0 && (
                  <p className="text-[11px] mt-1 text-amber-500">
                    Nessun preventivo accettato per "{clientName || "questo cliente"}".
                  </p>
                )}
              </div>
              <p className={`text-[11px] ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
                Importo precompilato dal totale del preventivo, modificabile per fatturare solo una quota.
              </p>
            </div>
          )}

          {typeError && (
            <p className="text-xs text-red-500">{typeError}</p>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
            <div>
              <label className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-600'}`}>Importo lordo (€)</label>
              <input
                className={`mt-1 w-full px-3 py-2 border rounded-lg text-sm ${
                  darkMode
                    ? 'border-neutral-700 bg-neutral-800 text-white'
                    : 'border-neutral-200 bg-white text-neutral-900'
                }`}
                value={grossAmount}
                onChange={(e) => handleAmountChange(e.target.value)}
                placeholder="1200,00"
                required
              />
            </div>

            <label className={`flex items-center gap-2 text-sm mt-6 ${darkMode ? 'text-white' : 'text-neutral-900'}`}>
              <input
                type="checkbox"
                checked={paid}
                onChange={(e) => setPaid(e.target.checked)}
              />
              Incassata
            </label>
          </div>

          {paid && (
            <div>
              <label className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-600'}`}>Data incasso (opzionale)</label>
              <input
                type="date"
                className={`mt-1 w-full px-3 py-2 border rounded-lg text-sm ${
                  darkMode
                    ? 'border-neutral-700 bg-neutral-800 text-white'
                    : 'border-neutral-200 bg-white text-neutral-900'
                }`}
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
              />
            </div>
          )}

          <div>
            <label className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-600'}`}>Note (opzionale)</label>
            <textarea
              className={`mt-1 w-full px-3 py-2 border rounded-lg text-sm min-h-[84px] ${
                darkMode
                  ? 'border-neutral-700 bg-neutral-800 text-white'
                  : 'border-neutral-200 bg-white text-neutral-900'
              }`}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Note interne..."
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              className={`px-3 py-2 rounded-lg border text-sm ${
                darkMode
                  ? 'border-neutral-700 bg-neutral-800 text-neutral-300 hover:bg-neutral-700'
                  : 'border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50'
              }`}
              onClick={onClose}
            >
              Annulla
            </button>
            <button
              type="submit"
              className="px-3 py-2 rounded-lg bg-forfy-500 text-white text-sm hover:bg-forfy-600"
            >
              {primaryLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
